<script setup lang="ts">
// NOTE: This component intentionally mutates the profile prop as part of its design.
// The parent component passes a buffer object that is expected to be modified in-place.
import { ref } from 'vue';
import { isEmpty } from 'lodash';
import { InvestorProfile, RestOperation } from '@/utils/helpers/rest';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { FormValidator } from '@/utils/helpers/input-form';
import axios, { AxiosError } from 'axios';
import { ApiError } from '@/utils/errors/api-error';

const props = defineProps<{
  crudAction: RestOperation;
  title: string;
  profile: Partial<NullableAll<InvestorProfile>>;
}>();

const emit = defineEmits<{
  (event: 'close-modal'): void;
}>();

const clErrors = ref<{ [index: string]: string }>({});
const svErrors = ref('');
const processing = ref(false);

async function createInvestorProfile(profile: InvestorProfile): Promise<void> {
  try {
    await axios.post(`/api/1/broker-admin/investor-profiles`, profile);
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function updateInvestorProfile(profile: InvestorProfile): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/investor-profiles/${profile.id}`, profile);
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function onSubmit(): Promise<void> {
  if (processing.value) {
    return;
  }
  processing.value = true;

  try {
    svErrors.value = '';
    clErrors.value = new FormValidator(props.profile, ['label']).check();

    if (isEmpty(clErrors.value)) {
      await doSubmit(props.profile as InvestorProfile);
    }
  } finally {
    processing.value = false;
  }
}

async function doSubmit(profileRecord: InvestorProfile): Promise<void> {
  try {
    if (props.crudAction === RestOperation.Create) {
      await createInvestorProfile(profileRecord);
    } else {
      await updateInvestorProfile(profileRecord);
    }

    closeModalDialog();
  } catch (e) {
    svErrors.value = errorString(e as Error);
  }
}

function closeModalDialog(): void {
  // allow parent to cleanup
  emit('close-modal');
}
</script>

<!-- eslint-disable vue/no-mutating-props -->
<!-- NOTE: This component intentionally mutates the profile prop as part of its design.
The parent component passes a buffer object that is expected to be modified in-place.
We kept this approach when we refactored from vue-class-component to Composition API.
@TODO: rework the entire state management to a more modern approach. -->
<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="800px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
  >
    <form class="form" novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{ $t(title) }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer v-if="svErrors">
            <VAlert dense type="error">{{ svErrors }}</VAlert>
          </VContainer>
          <VContainer>
            <VRow>
              <VCol>
                <VTextField
                  v-model="profile.label"
                  autofocus
                  :error-messages="clErrors.label"
                  :label="$t('investorProfile')"
                  placeholder="e.g. Pension Fund"
                  required
                />
              </VCol>
            </VRow>
          </VContainer>
        </VCardText>
        <VCardActions class="pt-16">
          <VBtn class="d-none d-sm-flex" color="secondary" text @click="closeModalDialog">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer />
          <VBtn
            color="primary"
            :disabled="processing"
            :loading="processing"
            min-width="120"
            type="submit"
            @click="onSubmit"
          >
            {{ $t('saveInvestorProfile') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </form>
  </VDialog>
</template>
